import { TpKernelDFData } from '../../helpers/kernelDF';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpDataTableColumn } from '../../../../data/interfaces';

// --- TYPES ---
export type CenterType = 'mean' | 'median';
export type WhiskerType = 'none' | 'min-max' | 'q25-q75' | 'mean-se' | 'mean-stdev';

// --- CONFIGURATION MAPS ---

interface CenterConfig {
  name: string;
  calculate: (k: TpKernelDFData) => number;
}

interface WhiskerConfig {
  name: string;
  calculate: (k: TpKernelDFData) => { lower: number; upper: number };
  getHoverText: (col: TpDataTableColumn, k: TpKernelDFData) => string;
}

const CENTER_CONFIGS: Record<CenterType, CenterConfig> = {
  mean: {
    name: 'Mean',
    calculate: (kernel) => kernel.meanValue,
  },
  median: {
    name: 'Median',
    calculate: (kernel) => kernel.q50,
  },
};

const WHISKER_CONFIGS: Record<WhiskerType, WhiskerConfig> = {
  none: {
    name: 'No whiskers',
    calculate: () => ({ lower: NaN, upper: NaN }),
    getHoverText: () => 'NA',
  },
  'min-max': {
    name: 'Min / Max',
    calculate: (kernel) => ({ lower: kernel.minValue, upper: kernel.maxValue }),
    getHoverText: (col, kernel) =>
      `${convertColumnDataToDisplayString(col, kernel.minValue)} / ${convertColumnDataToDisplayString(col, kernel.maxValue)}`,
  },
  'q25-q75': {
    name: 'Interquartile range',
    calculate: (kernel) => ({ lower: kernel.q25, upper: kernel.q75 }),
    getHoverText: (col, kernel) => convertColumnDataToDisplayString(col, kernel.q75 - kernel.q25),
  },
  'mean-se': {
    name: 'Standard error',
    calculate: (kernel) => ({ lower: kernel.meanValue - kernel.se, upper: kernel.meanValue + kernel.se }),
    getHoverText: (col, kernel) => convertColumnDataToDisplayString(col, kernel.se),
  },
  'mean-stdev': {
    name: 'Standard deviation',
    calculate: (kernel) => ({ lower: kernel.meanValue - kernel.stDev, upper: kernel.meanValue + kernel.stDev }),
    getHoverText: (col, kernel) => convertColumnDataToDisplayString(col, kernel.stDev),
  },
};

// --- EXPORTED UI OPTIONS ---

export const centerValueOptions = Object.entries(CENTER_CONFIGS).map(([id, config]) => ({
  id: id as CenterType,
  name: config.name,
}));

export const whiskersOptions = Object.entries(WHISKER_CONFIGS).map(([id, config]) => ({
  id: id as WhiskerType,
  name: config.name,
}));

// --- CENTER FUNCTIONS ---

export function calculateCenter(kernel: TpKernelDFData, type: CenterType = 'mean'): number {
  if (!kernel.hasData) return NaN;
  const config = CENTER_CONFIGS[type] ?? CENTER_CONFIGS['mean'];
  return config.calculate(kernel);
}

export function getCenterDisplayName(type: CenterType = 'mean'): string {
  const config = CENTER_CONFIGS[type] ?? CENTER_CONFIGS['mean'];
  return config.name;
}

export function getCenterHoverText(
  columnInfo: TpDataTableColumn,
  kernel: TpKernelDFData,
  type: CenterType = 'mean',
): string {
  const value = calculateCenter(kernel, type);
  return convertColumnDataToDisplayString(columnInfo, value);
}

// --- WHISKER FUNCTIONS ---

export function calculateWhiskers(
  kernel: TpKernelDFData,
  type: WhiskerType = 'none',
): { lower: number; upper: number } {
  if (!kernel.hasData) return { lower: NaN, upper: NaN };
  const config = WHISKER_CONFIGS[type] ?? WHISKER_CONFIGS['none'];
  return config.calculate(kernel);
}

export function getWhiskerDisplayName(type: WhiskerType = 'none'): string {
  const config = WHISKER_CONFIGS[type] ?? WHISKER_CONFIGS['none'];
  return config.name;
}

export function getWhiskerHoverText(
  columnInfo: TpDataTableColumn,
  kernel: TpKernelDFData,
  type: WhiskerType = 'none',
): string {
  const config = WHISKER_CONFIGS[type] ?? WHISKER_CONFIGS['none'];
  return config.getHoverText(columnInfo, kernel);
}
